/**
 * Cross-audit (ตรวจไขว้) sidebar support: explicit slice document id, optional metrics fallback,
 * and realtime sync for the active chart without fighting local edits.
 */
import React from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import type { ReportingMonth } from '../../lib/reportingMonth';
import { mergeCrossAuditChartRowFromDoc } from '../../lib/crossAuditMerge';
import {
  extractCrossAuditSlotFromDoc,
  findChartIndexByCrossAuditSlotWithFallback,
} from '../../lib/crossAuditSlot';
import { db } from '../../lib/firestore';
import { normalizeSubmissionShape } from '../../services/submissionShape';
import type { SubmissionDocument } from '../../services/submissions';
import { getFirestorePathDepartmentId, normalizeDepartmentId } from '../../utils/normalizeIds';

/** Same opt-in as cross-audit hydration fetch diag — avoids noisy logs in dev/Vitest unless explicitly enabled. */
const shouldLogCrossAuditSync = () =>
  typeof window !== 'undefined' && localStorage.getItem('debugHydration') === '1';

/** Firestore document id for a nursing cross-audit slice (matches ComplianceHub submit). */
export function buildCrossAuditNursingCategoryId(performedByDeptId: string, slot: number): string {
  const id = normalizeDepartmentId(performedByDeptId.trim()) || performedByDeptId.trim();
  return `CROSS_AUDIT_NURSING__${id}**${slot}`;
}

/**
 * When the slice has dashboard metrics but thin/empty answers, copy chart_1_focus / chart_1_treatments
 * into parallel-array slot 0 so mergeCrossAuditChartRowFromDoc can populate clinical fields.
 */
export function enrichCrossAuditDocFromMetrics(mutableDoc: SubmissionDocument): void {
  const m = (mutableDoc as { metrics?: Record<string, number> }).metrics;
  if (!m || typeof m !== 'object') return;
  const shape = normalizeSubmissionShape(mutableDoc as Record<string, unknown>);
  const row0 = shape.answers[0];
  const hasCells = Array.isArray(row0) && row0.some((c) => String(c ?? '').trim() !== '');
  if (hasCells) return;

  const f = m.chart_1_focus;
  const t = m.chart_1_treatments;
  const d = mutableDoc as Record<string, unknown>;
  if (f != null && Number.isFinite(f)) {
    if (!Array.isArray(d.focus)) d.focus = [];
    const arr = [...(d.focus as unknown[])];
    arr[0] = String(f);
    d.focus = arr;
  }
  if (t != null && Number.isFinite(t)) {
    if (!Array.isArray(d.treatments)) d.treatments = [];
    const arr = [...(d.treatments as unknown[])];
    arr[0] = String(t);
    d.treatments = arr;
  }
}

export type CrossAuditSliceFirestoreSyncProps = {
  /** Panel open + nursing form */
  enabled: boolean;
  /** Current chart is ตรวจไขว้ */
  isCrossAuditChart: boolean;
  /** Audited ward (submissions path); same as SidePanel departmentId */
  auditedDepartmentId: string | null | undefined;
  reportingMonth: ReportingMonth;
  /** Performer dept id for Firestore doc id (canonical, from department list when possible) */
  performedByDeptIdForFirestore: string | null | undefined;
  /** Slot parsed from title (1-based) */
  crossAuditSlot: number;
  chartTitles: string[];
  chartCount: number;
  /** Target row in the multi-chart form */
  chartIndex: number;
  /** While true, remote snapshot must not overwrite user input */
  hasLocalEdits: boolean;
  /** Wait until hydration finished so we do not race the storage hydration effect */
  hydrationLoading: boolean;
  /** Increment when server hydration refreshes — allows one re-sync after load */
  hydrationToken: string;
  onRemoteSlice: (doc: SubmissionDocument, resolvedChartIndex: number) => void;
};

/**
 * Subscribes to submissions/{auditedDept}/{month}/{CROSS_AUDIT_NURSING__…} while the cross-audit chart is active.
 * Does not filter deprecated mirrors (doc id is explicit). Unsubscribes on close or chart change.
 */
export function CrossAuditSliceFirestoreSync({
  enabled,
  isCrossAuditChart,
  auditedDepartmentId,
  reportingMonth,
  performedByDeptIdForFirestore,
  crossAuditSlot,
  chartTitles,
  chartCount,
  chartIndex,
  hasLocalEdits,
  hydrationLoading,
  hydrationToken,
  onRemoteSlice,
}: CrossAuditSliceFirestoreSyncProps) {
  const onRemoteSliceRef = React.useRef(onRemoteSlice);
  onRemoteSliceRef.current = onRemoteSlice;

  const gateRef = React.useRef({ hasLocalEdits, hydrationLoading });
  gateRef.current = { hasLocalEdits, hydrationLoading };

  React.useEffect(() => {
    if (!enabled || !isCrossAuditChart) return;
    const rawDept = auditedDepartmentId?.trim();
    const performer = performedByDeptIdForFirestore?.trim();
    if (!rawDept || !performer || performer === 'unknown') return;

    const auditedPathId = getFirestorePathDepartmentId(rawDept) ?? normalizeDepartmentId(rawDept);
    if (!auditedPathId || !reportingMonth) return;

    const categoryId = buildCrossAuditNursingCategoryId(performer, crossAuditSlot);
    const docRef = doc(db, 'submissions', auditedPathId, reportingMonth, categoryId);

    if (shouldLogCrossAuditSync()) {
      console.info('[CrossAuditSync] listening', { path: docRef.path, chartIndex, hydrationToken });
    }

    const unsub = onSnapshot(
      docRef,
      (snap) => {
        if (!snap.exists()) return;
        if (gateRef.current.hasLocalEdits || gateRef.current.hydrationLoading) return;
        const data = snap.data() as SubmissionDocument;
        const slot = extractCrossAuditSlotFromDoc(data as Record<string, unknown>);
        if (slot != null && slot !== crossAuditSlot) return;

        const resolvedIdx = findChartIndexByCrossAuditSlotWithFallback(chartTitles, crossAuditSlot, chartCount);
        if (resolvedIdx < 0) return;

        const enriched: SubmissionDocument = { ...data };
        enrichCrossAuditDocFromMetrics(enriched);
        onRemoteSliceRef.current(enriched, resolvedIdx);
      },
      (err) => {
        if (shouldLogCrossAuditSync()) {
          console.info('[CrossAuditSync] snapshot error', err);
        }
      },
    );

    return () => unsub();
  }, [
    auditedDepartmentId,
    chartCount,
    chartIndex,
    chartTitles,
    crossAuditSlot,
    enabled,
    hydrationToken,
    isCrossAuditChart,
    performedByDeptIdForFirestore,
    reportingMonth,
  ]);

  return null;
}

export type CrossAuditRowPatch = {
  chartIndex: number;
  answersRow?: string[];
  focus?: string;
  treatments?: string;
  disease?: string;
  dates?: string;
  shifts?: string;
  hn?: string;
  suggestions?: string;
  problems?: string[];
  submitted?: boolean;
  version?: number;
};

/** Derive SidePanel state updates from a slice doc (same merge rules as hydration). */
export function crossAuditSliceToRowPatch(
  chartIndex: number,
  crossDoc: SubmissionDocument,
): CrossAuditRowPatch | null {
  const stub: SubmissionDocument = {
    answers: [],
    chartTitles: [],
  };
  mergeCrossAuditChartRowFromDoc(stub, chartIndex, crossDoc);
  const t = stub as Record<string, unknown>;
  const answers = t.answers as string[][] | undefined;
  const answersRow = Array.isArray(answers?.[chartIndex]) ? (answers[chartIndex] as string[]) : undefined;

  const strAt = (key: 'focus' | 'treatments' | 'disease' | 'dates' | 'shifts' | 'hn' | 'suggestions') => {
    const arr = t[key] as string[] | undefined;
    return typeof arr?.[chartIndex] === 'string' ? arr[chartIndex] : undefined;
  };

  const problemsFull = t.problems as string[][] | undefined;
  const problemsRow = Array.isArray(problemsFull?.[chartIndex]) ? problemsFull[chartIndex] : undefined;

  const sub = Array.isArray(crossDoc.submitted) ? crossDoc.submitted[0] === true : undefined;
  const ver = Array.isArray(crossDoc.versions) && typeof crossDoc.versions[0] === 'number' ? crossDoc.versions[0] : undefined;

  if (
    !answersRow &&
    strAt('focus') === undefined &&
    strAt('treatments') === undefined &&
    strAt('disease') === undefined &&
    strAt('dates') === undefined &&
    strAt('shifts') === undefined &&
    strAt('hn') === undefined &&
    strAt('suggestions') === undefined &&
    problemsRow === undefined &&
    sub === undefined &&
    ver === undefined
  ) {
    return null;
  }

  return {
    chartIndex,
    answersRow,
    focus: strAt('focus'),
    treatments: strAt('treatments'),
    disease: strAt('disease'),
    dates: strAt('dates'),
    shifts: strAt('shifts'),
    hn: strAt('hn'),
    suggestions: strAt('suggestions'),
    problems: problemsRow,
    submitted: sub,
    version: ver,
  };
}
